"use client";

import Link from "next/link";
import { CheckCircle, Clock, Map, Target, ArrowRight } from "lucide-react";
import ServicesGrid from "@/components/ServicesGrid";

export default function GenAIBlueprintPage() {
  return (
    <div className="min-h-screen bg-slate-50 pt-24">
      
      {/* --- HERO HEADER --- */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 pt-10 pb-16 md:pt-16 md:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* LEFT COLUMN: TEXT */}
            <div className="text-left">
              <h1 className="text-4xl md:text-6xl font-bold font-sans text-slate-900 mb-6 flex items-center justify-start gap-0 tracking-tight">
                Gen AI Blueprint
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-red-600 h-[1em] w-[1.15em] -ml-5">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5 l9 7 -9 7" />
                </svg>
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-xl">
                Turn your AI vision into a clear, actionable roadmap in just 90 minutes. Stop guessing and start building.
              </p>
              <div className="flex flex-wrap justify-start gap-4">
                <Link href="/contact-us" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-red-600 rounded-full hover:bg-red-700 hover:shadow-lg hover:-translate-y-1">
                  Schedule Workshop
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>

            {/* RIGHT COLUMN: IMAGE 
               UPDATED: Added max-h-80 w-auto. This limits the height to 320px, 
               reducing the container height and effectively pulling the left-side text UP.
            */}
            <div className="flex justify-center lg:justify-end">
              <img 
                src="https://placehold.co/800x600/ffffff/cbd5e1?text=AI+Blueprint+Visual" 
                alt="Gen AI Blueprint Visualization"
                className="w-auto max-h-80 object-contain shadow-sm rounded-xl"
              />
            </div>

          </div>
        </div>
      </div>

      {/* ... (Rest of page code is unchanged from previous turn) ... */}
      {/* --- SECTION 1: ASSESSMENT --- */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-16 md:pt-10 md:pb-24">
            <div className="text-center mb-12">
                <span className="text-red-600 font-bold tracking-wider uppercase text-sm">Assess</span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">Stop Guessing. Start Building.</h2>
                <p className="text-slate-600 max-w-2xl mx-auto mt-4">
                The Blueprint is a guided strategy session designed to identify high-impact AI use cases, ensuring you invest in growth, not just hype.
                </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-sm flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mb-6"><Clock className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">90 Minutes</h3>
                <p className="text-slate-600">A structured, high-intensity workshop that cuts through the noise.</p>
              </div>
              <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-sm flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mb-6"><Target className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Top 3 Use Cases</h3>
                <p className="text-slate-600">We prioritize initiatives based on ROI and technical feasibility.</p>
              </div>
              <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-sm flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mb-6"><Map className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Clear Roadmap</h3>
                <p className="text-slate-600">Walk away with an execution plan, timeline, and resource requirements.</p>
              </div>
            </div>
      </div>

      {/* --- SECTION 2: THE PROCESS (Dark Mode) --- */}
      <div className="bg-slate-900 pt-8 pb-16 md:pt-10 md:pb-24 text-white">
        <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
                <span className="text-red-500 font-bold tracking-wider uppercase text-sm">Execute</span>
                <h2 className="text-3xl md:text-4xl font-bold mt-2">The Blueprint Process</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                    <div className="flex gap-4">
                    <div className="shrink-0 h-10 w-10 rounded-full bg-slate-800 border border-slate-700 text-white flex items-center justify-center font-bold">1</div>
                    <div><h3 className="text-xl font-bold text-white mb-2">Discovery</h3><p className="text-slate-400">We capture every active or planned GenAI initiative. We ignore the technical jargon and focus purely on business outcomes.</p></div>
                    </div>
                    <div className="flex gap-4">
                    <div className="shrink-0 h-10 w-10 rounded-full bg-slate-800 border border-slate-700 text-white flex items-center justify-center font-bold">2</div>
                    <div><h3 className="text-xl font-bold text-white mb-2">Prioritization</h3><p className="text-slate-400">Using our proprietary matrix, we rank each initiative by Business Impact vs. Technical Feasibility.</p></div>
                    </div>
                    <div className="flex gap-4">
                    <div className="shrink-0 h-10 w-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold">3</div>
                    <div><h3 className="text-xl font-bold text-white mb-2">The Roadmap</h3><p className="text-slate-400">We classify projects into "Execute Now," "Near Term," and "Future Vision" and assign ownership.</p></div>
                    </div>
                </div>
                <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
                    <h3 className="text-lg font-bold text-white mb-6 border-b border-slate-700 pb-4">Key Deliverables</h3>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" /><span className="text-slate-300">Prioritization Matrix (Ranked by ROI)</span></li>
                        <li className="flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" /><span className="text-slate-300">Technology Readiness Plan (TRP)</span></li>
                        <li className="flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" /><span className="text-slate-300">AWS Funding & Credits Analysis</span></li>
                    </ul>
                </div>
            </div>
        </div>
      </div>

      {/* --- SECTION 3: BENEFITS/WHY --- */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-16 md:pt-10 md:pb-24">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">Why You Need a Blueprint</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            <div><div className="h-12 w-12 bg-red-50 rounded-lg flex items-center justify-center mb-6"><Target className="h-6 w-6 text-red-600" /></div><h3 className="text-xl font-bold text-slate-900 mb-3">High-Impact Focus</h3><p className="text-slate-600">Align executive and technical teams on the use cases that actually drive revenue.</p></div>
            <div><div className="h-12 w-12 bg-red-50 rounded-lg flex items-center justify-center mb-6"><Map className="h-6 w-6 text-red-600" /></div><h3 className="text-xl font-bold text-slate-900 mb-3">Defined Accountability</h3><p className="text-slate-600">Every project gets a timeline and an owner. Don't get stuck in "research mode."</p></div>
            <div><div className="h-12 w-12 bg-red-50 rounded-lg flex items-center justify-center mb-6"><CheckCircle className="h-6 w-6 text-red-600" /></div><h3 className="text-xl font-bold text-slate-900 mb-3">AWS Acceleration</h3><p className="text-slate-600">Leverage our partner status to identify AWS funding and credits to offset costs.</p></div>
          </div>
      </div>

      <ServicesGrid />
    </div>
  );
}